import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import '../../config/api'; // ensure axios interceptors (JWT) are configured
import {
  Play,
  RefreshCcw,
  ChevronDown,
  ChevronRight,
  Globe,
  Eye,
  Bot,
  AlertCircle,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import AdminRepairsPanel from './AdminRepairsPanel';
import EndOfDayPanel from './EndOfDayPanel';
import { getTenantToday } from '../../utils/dateUtils';

import { DateField } from '../shared/DateField';

/**
 * Admin › Batch Jobs
 *
 * Every @Scheduled job in the backend, in real firing order, with a Run button
 * that calls the same service method the cron calls with an explicit as-of
 * date. Manual runs dispatched through /api/admin/scheduled and cron firings
 * (recorded by ScheduledJobRunAspect, triggered_by = 'cron') both land in
 * admin_batch_job_run (V545) and feed the Last run column and the run log.
 * Agent triggers fired from this page go to /api/autonomous-workflows and are
 * recorded only when their cron fires.
 *
 * Built for the Phase 2 day-cycle lane: the operator fires the cycle one job
 * at a time on a chosen date, diffs the ledgers, then fires it again to prove
 * idempotency.
 */

interface ParamSpec {
  name: string;
  type: 'date' | 'decimal' | string;
  required: boolean;
  description: string;
}

interface JobDescriptor {
  id: string;
  name: string;
  block: string;
  fireTime: string;
  cron: string;
  zone: string;
  kind: 'SCHEDULED' | 'AGENT' | 'OBSERVE';
  service: string;
  description: string;
  asOfDefault: string;
  tenantScope: 'ALL_TENANTS' | 'CALLER_TENANT' | 'GLOBAL';
  externalCalls: boolean;
  writes: string[];
  params: ParamSpec[];
  triggerPath: string | null;
}

interface RunRow {
  id: number;
  jobId: string;
  asOf: string | null;
  params: Record<string, unknown> | null;
  triggeredBy: string;
  callerTenantId: number | null;
  startedAt: string;
  finishedAt: string | null;
  status: 'RUNNING' | 'SUCCESS' | 'FAILED';
  durationMs: number | null;
  result: unknown;
  error: string | null;
}

interface LocalOutcome {
  status: 'SUCCESS' | 'FAILED';
  at: string;
  durationMs?: number;
  runId?: number;
  payload: unknown;
}

/** The book's business date: what a job runs as of unless the operator picks another. */
const todayIso = () => getTenantToday();

const BLOCK_ORDER = ['Pre-open', 'Morning agents', 'Intraday', 'Close', 'EOD', 'End of day', 'Evening', 'Night', 'Overnight'];

const fmtTs = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const fmtMs = (ms: number | null | undefined) => (ms == null ? '—' : ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`);

const errorText = (e: unknown): string => {
  if (axios.isAxiosError(e)) {
    const data = e.response?.data as { error?: string; message?: string } | undefined;
    return data?.error || data?.message || e.message;
  }
  return e instanceof Error ? e.message : String(e);
};

/** Parameters set by the page-level controls above the job list; every other declared parameter gets its own input on the job. */
const PAGE_LEVEL_PARAMS = ['asOf', 'sofr', 'cutoff'];

const BatchJobsScreen: React.FC = () => {
  const [jobs, setJobs] = useState<JobDescriptor[]>([]);
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [asOf, setAsOf] = useState<string>(todayIso());
  const [useAsOf, setUseAsOf] = useState<boolean>(true);
  const [sofr, setSofr] = useState<string>('');
  const [cutoff, setCutoff] = useState<string>('');
  // F-MK-036: per-job values for parameters the page-level controls do not cover (e.g. abf-data-feed facilityId).
  const [jobParams, setJobParams] = useState<Record<string, Record<string, string>>>({});
  const [runningId, setRunningId] = useState<string | null>(null);
  const [outcomes, setOutcomes] = useState<Record<string, LocalOutcome>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [expandedRun, setExpandedRun] = useState<Record<number, boolean>>({});
  const [showAgents, setShowAgents] = useState(true);

  const fetchJobs = useCallback(async () => {
    setLoadingJobs(true);
    setLoadError(null);
    try {
      const res = await axios.get<JobDescriptor[]>('/api/admin/scheduled/jobs');
      setJobs(res.data);
    } catch (e) {
      setLoadError(errorText(e));
    } finally {
      setLoadingJobs(false);
    }
  }, []);

  const fetchRuns = useCallback(async () => {
    try {
      const res = await axios.get<RunRow[]>('/api/admin/scheduled/runs', { params: { limit: 100 } });
      setRuns(res.data);
    } catch (e) {
      // The run log is secondary; surface the error in the log panel only.
      setRuns([]);
      setLoadError(prev => prev ?? `Run log unavailable: ${errorText(e)}`);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
    fetchRuns();
  }, [fetchJobs, fetchRuns]);

  const lastRunByJob = useMemo(() => {
    const m: Record<string, RunRow> = {};
    for (const r of runs) {
      if (!m[r.jobId]) m[r.jobId] = r; // runs come newest first
    }
    return m;
  }, [runs]);

  const grouped = useMemo(() => {
    const byBlock: Record<string, JobDescriptor[]> = {};
    for (const j of jobs) {
      if (!showAgents && j.kind !== 'SCHEDULED') continue;
      if (!byBlock[j.block]) byBlock[j.block] = [];
      byBlock[j.block].push(j);
    }
    // F-MK-036: a block missing from the order (the data feed's Evening block was) is drawn last,
    // never dropped; a job the catalogue serves is always on the page.
    const order = [...BLOCK_ORDER, ...Object.keys(byBlock).filter(b => !BLOCK_ORDER.includes(b)).sort()];
    return order.filter(b => byBlock[b]?.length).map(b => ({ block: b, jobs: byBlock[b] }));
  }, [jobs, showAgents]);

  const missingRequired = (job: JobDescriptor): string | null => {
    for (const p of job.params) {
      if (!p.required) continue;
      if (p.name === 'sofr' && !sofr.trim()) return 'SOFR fixing required';
      if (!PAGE_LEVEL_PARAMS.includes(p.name) && !(jobParams[job.id]?.[p.name] || '').trim()) return `${p.name} required`;
    }
    return null;
  };

  const runJob = async (job: JobDescriptor) => {
    if (!job.triggerPath || job.kind === 'OBSERVE') return;
    const missing = missingRequired(job);
    if (missing) {
      setOutcomes(o => ({ ...o, [job.id]: { status: 'FAILED', at: new Date().toISOString(), payload: missing } }));
      return;
    }
    setRunningId(job.id);
    const params: Record<string, string> = {};
    if (job.kind === 'SCHEDULED') {
      if (useAsOf && job.params.some(p => p.name === 'asOf')) params.asOf = asOf;
      if (job.params.some(p => p.name === 'sofr')) params.sofr = sofr.trim();
      if (job.params.some(p => p.name === 'cutoff') && cutoff) params.cutoff = cutoff;
      for (const p of job.params) {
        if (PAGE_LEVEL_PARAMS.includes(p.name)) continue;
        const value = (jobParams[job.id]?.[p.name] || '').trim();
        if (value) params[p.name] = value;
      }
    }
    try {
      const res = await axios.post(job.triggerPath, null, { params });
      const body = res.data as { runId?: number; durationMs?: number; result?: unknown };
      setOutcomes(o => ({
        ...o,
        [job.id]: {
          status: 'SUCCESS',
          at: new Date().toISOString(),
          durationMs: body?.durationMs,
          runId: body?.runId,
          payload: job.kind === 'SCHEDULED' ? body?.result ?? body : body,
        },
      }));
      setExpanded(x => ({ ...x, [job.id]: true }));
    } catch (e) {
      setOutcomes(o => ({ ...o, [job.id]: { status: 'FAILED', at: new Date().toISOString(), payload: errorText(e) } }));
      setExpanded(x => ({ ...x, [job.id]: true }));
    } finally {
      setRunningId(null);
      if (job.kind === 'SCHEDULED') fetchRuns();
    }
  };

  const KindBadge: React.FC<{ job: JobDescriptor }> = ({ job }) => {
    if (job.kind === 'AGENT')
      return (
        <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold bg-violet-500/15 text-violet-300 border border-violet-500/30">
          <Bot className="w-3 h-3" /> AGENT
        </span>
      );
    if (job.kind === 'OBSERVE')
      return (
        <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold bg-slate-500/15 text-slate-300 border border-slate-500/30">
          <Eye className="w-3 h-3" /> OBSERVE
        </span>
      );
    return null;
  };

  const StatusPill: React.FC<{ status: 'SUCCESS' | 'FAILED' | 'RUNNING' }> = ({ status }) =>
    status === 'SUCCESS' ? (
      <span className="inline-flex items-center gap-1 text-emerald-400 text-xs font-semibold"><CheckCircle2 className="w-3.5 h-3.5" /> SUCCESS</span>
    ) : status === 'FAILED' ? (
      <span className="inline-flex items-center gap-1 text-rose-400 text-xs font-semibold"><AlertCircle className="w-3.5 h-3.5" /> FAILED</span>
    ) : (
      <span className="inline-flex items-center gap-1 text-amber-300 text-xs font-semibold"><Loader2 className="w-3.5 h-3.5 animate-spin" /> RUNNING</span>
    );

  return (
    <div className="space-y-6" data-testid="batch-jobs-screen">
      <EndOfDayPanel />

      {/* Controls */}
      <div className="rounded-xl border border-slate-700/60 bg-slate-900/60 p-5">
        <div className="flex flex-wrap items-end gap-6">
          <div>
            <h2 className="text-lg font-bold text-white">Batch Jobs</h2>
            <p className="text-sm text-slate-400 mt-0.5">
              Every job in firing order (America/New_York). Jobs in the End of day block have no clock of their own: the end-of-day run above drives them on the business date. Run fires one job by itself, as of the date below.
            </p>
          </div>
          <div className="flex items-end gap-4 ml-auto">
            <label className="flex flex-col text-xs text-slate-400 gap-1">
              <span className="flex items-center gap-2">
                <input type="checkbox" checked={useAsOf} onChange={e => setUseAsOf(e.target.checked)} className="accent-blue-500" />
                Pass as-of date
              </span>
              <DateField
                compact
                aria-label="As-of date"
                value={asOf}
                disabled={!useAsOf}
                onChange={iso => setAsOf(iso)}
              />
            </label>
            <label className="flex flex-col text-xs text-slate-400 gap-1">
              SOFR fixing (rate reset)
              <input
                type="text"
                inputMode="decimal"
                placeholder="0.0525"
                value={sofr}
                onChange={e => setSofr(e.target.value)}
                className="bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-sm text-white w-28"
                data-testid="batch-sofr"
              />
            </label>
            <label className="flex flex-col text-xs text-slate-400 gap-1">
              Archive cutoff (audit archive)
              <DateField
                compact
                aria-label="Archive cutoff"
                value={cutoff ?? ''}
                onChange={iso => setCutoff(iso)}
              />
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-400 pb-2">
              <input type="checkbox" checked={showAgents} onChange={e => setShowAgents(e.target.checked)} className="accent-blue-500" />
              Show agents
            </label>
            <button
              onClick={() => { fetchJobs(); fetchRuns(); }}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm border border-slate-600"
            >
              <RefreshCcw className="w-4 h-4" /> Refresh
            </button>
          </div>
        </div>
        {!useAsOf && (
          <p className="mt-3 text-xs text-amber-300">
            As-of is off: each job uses its default, the book's business date unless the “Default” column says otherwise.
          </p>
        )}
        {loadError && (
          <p className="mt-3 text-sm text-rose-400 flex items-center gap-2"><AlertCircle className="w-4 h-4" /> {loadError}</p>
        )}
      </div>

      {/* Catalog */}
      {loadingJobs ? (
        <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading job catalog…</div>
      ) : (
        grouped.map(({ block, jobs: blockJobs }) => (
          <div key={block} className="rounded-xl border border-slate-700/60 bg-slate-900/40 overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-700/60 flex items-center justify-between">
              <h3 className="text-sm font-bold text-white tracking-wide uppercase">{block}</h3>
              <span className="text-xs text-slate-500">{blockJobs.length} job{blockJobs.length === 1 ? '' : 's'}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 uppercase tracking-wider">
                    <th className="px-5 py-2 w-8"></th>
                    <th className="px-2 py-2 w-24">Fires</th>
                    <th className="px-2 py-2">Job</th>
                    <th className="px-2 py-2 w-28">Scope</th>
                    <th className="px-2 py-2 w-28">Default</th>
                    <th className="px-2 py-2">Writes</th>
                    <th className="px-2 py-2 w-44">Last run</th>
                    <th className="px-5 py-2 w-28 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {blockJobs.map(job => {
                    const local = outcomes[job.id];
                    const last = lastRunByJob[job.id];
                    const isOpen = !!expanded[job.id];
                    const isRunning = runningId === job.id;
                    const canRun = job.kind !== 'OBSERVE' && !!job.triggerPath;
                    return (
                      <React.Fragment key={job.id}>
                        <tr className={cn('border-t border-slate-800 hover:bg-slate-800/40', isOpen && 'bg-slate-800/30')} data-testid={`job-row-${job.id}`}>
                          <td className="px-5 py-2.5 align-top">
                            <button onClick={() => setExpanded(x => ({ ...x, [job.id]: !isOpen }))} className="text-slate-500 hover:text-slate-200">
                              {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            </button>
                          </td>
                          <td className="px-2 py-2.5 align-top font-mono text-xs text-slate-300 whitespace-nowrap">{job.fireTime}</td>
                          <td className="px-2 py-2.5 align-top">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-white">{job.name}</span>
                              <KindBadge job={job} />
                              {job.externalCalls && (
                                <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold bg-amber-500/15 text-amber-300 border border-amber-500/30" title="Makes live external calls">
                                  <Globe className="w-3 h-3" /> EXTERNAL
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-400 mt-0.5">{job.description}</div>
                            <div className="text-[11px] text-slate-500 font-mono mt-0.5">{job.service} · {job.cron}</div>
                          </td>
                          <td className="px-2 py-2.5 align-top text-xs text-slate-300">{job.tenantScope.replace('_', ' ').toLowerCase()}</td>
                          <td className="px-2 py-2.5 align-top text-xs text-slate-300">{job.asOfDefault}</td>
                          <td className="px-2 py-2.5 align-top text-xs text-slate-400 font-mono">{job.writes.join(', ') || '—'}</td>
                          <td className="px-2 py-2.5 align-top text-xs">
                            {local ? (
                              <div className="space-y-0.5">
                                <StatusPill status={local.status} />
                                <div className="text-slate-500">{fmtTs(local.at)} · {fmtMs(local.durationMs)}{local.runId ? ` · run #${local.runId}` : ''}</div>
                              </div>
                            ) : last ? (
                              <div className="space-y-0.5">
                                <StatusPill status={last.status} />
                                <div className="text-slate-500">
                                  {fmtTs(last.startedAt)} · {fmtMs(last.durationMs)} ·{' '}
                                  <span className={cn(last.triggeredBy === 'cron' ? 'text-sky-300' : 'text-slate-300')}>{last.triggeredBy}</span>
                                </div>
                              </div>
                            ) : (
                              <span className="text-slate-600">never</span>
                            )}
                          </td>
                          <td className="px-5 py-2.5 align-top text-right">
                            {canRun ? (
                              <button
                                onClick={() => runJob(job)}
                                disabled={runningId !== null}
                                className={cn(
                                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors',
                                  isRunning
                                    ? 'bg-blue-500/20 text-blue-200 border-blue-500/40'
                                    : 'bg-blue-600 hover:bg-blue-500 text-white border-blue-500 disabled:opacity-40 disabled:cursor-not-allowed'
                                )}
                                data-testid={`run-${job.id}`}
                              >
                                {isRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                                {isRunning ? 'Running' : 'Run'}
                              </button>
                            ) : (
                              <span className="text-xs text-slate-600">cron only</span>
                            )}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="border-t border-slate-800/60 bg-slate-950/40">
                            <td colSpan={8} className="px-10 py-3">
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-xs">
                                <div className="space-y-1">
                                  <div className="text-slate-500 uppercase tracking-wider text-[10px]">Trigger</div>
                                  <div className="font-mono text-slate-300">{job.triggerPath ? `POST ${job.triggerPath}` : 'none — cron only'}</div>
                                  {job.params.length > 0 && (
                                    <ul className="mt-1 space-y-0.5">
                                      {job.params.map(p => (
                                        <li key={p.name} className="text-slate-400">
                                          <span className="font-mono text-slate-200">{p.name}</span>
                                          <span className="text-slate-500"> ({p.type}{p.required ? ', required' : ''})</span> — {p.description}
                                          {job.kind === 'SCHEDULED' && !PAGE_LEVEL_PARAMS.includes(p.name) && (
                                            <input
                                              type="text"
                                              aria-label={`${job.id} ${p.name}`}
                                              placeholder={p.type}
                                              value={jobParams[job.id]?.[p.name] || ''}
                                              onChange={e => setJobParams(x => ({ ...x, [job.id]: { ...(x[job.id] || {}), [p.name]: e.target.value } }))}
                                              className="ml-2 w-32 rounded border border-slate-600 bg-slate-900 px-2 py-0.5 font-mono text-xs text-slate-100"
                                            />
                                          )}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                                <div className="space-y-1 min-w-0">
                                  <div className="text-slate-500 uppercase tracking-wider text-[10px]">
                                    {local ? `Result (${fmtTs(local.at)})` : last ? `Last recorded result (${fmtTs(last.startedAt)})` : 'Result'}
                                  </div>
                                  <pre className="bg-slate-900 border border-slate-800 rounded p-3 overflow-x-auto max-h-72 text-[11px] text-slate-200 whitespace-pre-wrap break-words">
                                    {local
                                      ? typeof local.payload === 'string' ? local.payload : JSON.stringify(local.payload, null, 2)
                                      : last
                                        ? last.error ?? JSON.stringify(last.result, null, 2)
                                        : 'No run yet.'}
                                  </pre>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      {/* Run log */}
      <div className="rounded-xl border border-slate-700/60 bg-slate-900/40 overflow-hidden" data-testid="batch-run-log">
        <div className="px-5 py-3 border-b border-slate-700/60 flex items-center justify-between">
          <h3 className="text-sm font-bold text-white tracking-wide uppercase">Run log</h3>
          <span className="text-xs text-slate-500">admin_batch_job_run · cron + manual · newest first · {runs.length} shown</span>
        </div>
        {runs.length === 0 ? (
          <div className="px-5 py-6 text-sm text-slate-500">No recorded runs yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 uppercase tracking-wider">
                  <th className="px-5 py-2 w-8"></th>
                  <th className="px-2 py-2 w-16">Run</th>
                  <th className="px-2 py-2">Job</th>
                  <th className="px-2 py-2 w-28">As of</th>
                  <th className="px-2 py-2 w-28">Status</th>
                  <th className="px-2 py-2 w-24">Duration</th>
                  <th className="px-2 py-2 w-44">Started</th>
                  <th className="px-2 py-2 w-32">By</th>
                  <th className="px-5 py-2 w-20">Tenant</th>
                </tr>
              </thead>
              <tbody>
                {runs.map(r => {
                  const open = !!expandedRun[r.id];
                  return (
                    <React.Fragment key={r.id}>
                      <tr className="border-t border-slate-800 hover:bg-slate-800/40">
                        <td className="px-5 py-2">
                          <button onClick={() => setExpandedRun(x => ({ ...x, [r.id]: !open }))} className="text-slate-500 hover:text-slate-200">
                            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                        </td>
                        <td className="px-2 py-2 font-mono text-xs text-slate-400">#{r.id}</td>
                        <td className="px-2 py-2 text-white font-medium">{r.jobId}</td>
                        <td className="px-2 py-2 font-mono text-xs text-slate-300">{r.asOf ?? '—'}</td>
                        <td className="px-2 py-2"><StatusPill status={r.status} /></td>
                        <td className="px-2 py-2 text-xs text-slate-300">{fmtMs(r.durationMs)}</td>
                        <td className="px-2 py-2 text-xs text-slate-400">{fmtTs(r.startedAt)}</td>
                        <td className="px-2 py-2 text-xs text-slate-300">{r.triggeredBy}</td>
                        <td className="px-5 py-2 text-xs text-slate-400">{r.callerTenantId ?? '—'}</td>
                      </tr>
                      {open && (
                        <tr className="border-t border-slate-800/60 bg-slate-950/40">
                          <td colSpan={9} className="px-10 py-3">
                            {r.params && Object.keys(r.params).length > 0 && (
                              <div className="text-xs text-slate-400 mb-2">params: <span className="font-mono text-slate-200">{JSON.stringify(r.params)}</span></div>
                            )}
                            <pre className="bg-slate-900 border border-slate-800 rounded p-3 overflow-x-auto max-h-72 text-[11px] text-slate-200 whitespace-pre-wrap break-words">
                              {r.error ?? JSON.stringify(r.result, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <AdminRepairsPanel />
    </div>
  );
};

export default BatchJobsScreen;
